import clsx from 'clsx';
import React, { ForwardedRef, forwardRef, ReactNode } from 'react';

import useIntersectionObserver, { UseIntersectionObserverOptions } from '@pflegia/ui/hooks/use-intersection-observer';

export interface ScrollableSectionProps {
  anchorClassName?: string;
  onIntersectionChange?: (sectionId: string, entry: IntersectionObserverEntry) => void;
  rootMargin?: string;
  sectionId: string;
  children?: ReactNode;
}

const ScrollableSectionComponent = forwardRef<HTMLElement, ScrollableSectionProps>(
  ({ anchorClassName, children, onIntersectionChange, rootMargin, sectionId }, forwardedRef) => {
    const sectionRef = React.useRef<HTMLElement>(null);
    const handleIntersection = React.useCallback<UseIntersectionObserverOptions['callback']>(
      entries => {
        entries.forEach(entry => {
          onIntersectionChange?.(sectionId, entry);
        });
      },
      [onIntersectionChange, sectionId],
    );
    useIntersectionObserver({
      callback: handleIntersection,
      ref: sectionRef,
      rootMargin,
      type: 'both',
    });

    return (
      <section
        ref={element => {
          sectionRef.current = element;
          if (typeof forwardedRef === 'function') forwardedRef(element);
          else if (forwardedRef) forwardedRef.current = element;
        }}>
        <a className={clsx('relative', anchorClassName)} id={sectionId} />
        {children}
      </section>
    );
  },
);
ScrollableSectionComponent.displayName = 'ScrollableSection';

const ScrollableSection = React.memo<ScrollableSectionProps & { ref?: ForwardedRef<HTMLElement> }>(ScrollableSectionComponent);

export default ScrollableSection;
